import React from 'react'
import {Link} from 'react-router-dom'
import {Spinner} from 'react-bootstrap'
import {KTIcon} from '../../../_metronic/helpers/components/KTIcon'

interface WelcomeDocument {
  pageId: string
  title: string
  shortDescription: string
  order: number
  icon: string
}

interface WelcomeCategory {
  name: string
  displayName: string
  icon: string
  order: number
  documents: WelcomeDocument[]
}

interface Props {
  categories: WelcomeCategory[]
  isLoading: boolean
}

/**
 * Per-category visual theme. Each card gets:
 *  - a lead icon (KTIcon) shown large next to the title,
 *  - a tint colour used for the headline,
 *  - an animated SVG background keyed off the category.
 */
interface CategoryTheme {
  icon: string
  accent: string
  bg: React.ReactNode
}

const FALLBACK_THEME: CategoryTheme = {
  icon: 'book-open',
  accent: '#009EF7',
  bg: (
    <svg viewBox='0 0 240 240' xmlns='http://www.w3.org/2000/svg'>
      <circle cx='60' cy='60' r='40' fill='#009EF7'>
        <animate attributeName='r' values='40;50;40' dur='6s' repeatCount='indefinite' />
      </circle>
      <circle cx='180' cy='180' r='30' fill='#009EF7'>
        <animate attributeName='r' values='30;42;30' dur='7s' repeatCount='indefinite' />
      </circle>
    </svg>
  ),
}

const THEMES: Record<string, CategoryTheme> = {
  DASHBOARD: {
    icon: 'chart-simple',
    accent: '#009EF7',
    bg: (
      <svg viewBox='0 0 240 240' xmlns='http://www.w3.org/2000/svg'>
        <rect x='40' y='140' width='24' height='60' rx='4' fill='#009EF7'>
          <animate attributeName='height' values='60;100;60' dur='3s' repeatCount='indefinite' />
          <animate attributeName='y' values='140;100;140' dur='3s' repeatCount='indefinite' />
        </rect>
        <rect x='80' y='100' width='24' height='100' rx='4' fill='#009EF7' opacity='0.8'>
          <animate attributeName='height' values='100;70;100' dur='3.5s' repeatCount='indefinite' />
          <animate attributeName='y' values='100;130;100' dur='3.5s' repeatCount='indefinite' />
        </rect>
        <rect x='120' y='120' width='24' height='80' rx='4' fill='#009EF7' opacity='0.6'>
          <animate attributeName='height' values='80;110;80' dur='4s' repeatCount='indefinite' />
          <animate attributeName='y' values='120;90;120' dur='4s' repeatCount='indefinite' />
        </rect>
        <rect x='160' y='80' width='24' height='120' rx='4' fill='#009EF7' opacity='0.5'>
          <animate attributeName='height' values='120;90;120' dur='4.5s' repeatCount='indefinite' />
          <animate attributeName='y' values='80;110;80' dur='4.5s' repeatCount='indefinite' />
        </rect>
      </svg>
    ),
  },
  SALES: {
    icon: 'handcart',
    accent: '#50CD89',
    bg: (
      <svg viewBox='0 0 240 240' xmlns='http://www.w3.org/2000/svg'>
        <circle cx='80' cy='80' r='50' fill='#50CD89'>
          <animate attributeName='r' values='50;60;50' dur='4s' repeatCount='indefinite' />
        </circle>
        <path d='M170,140 L200,110 L200,180 L170,210 Z' fill='#50CD89' opacity='0.5'>
          <animateTransform attributeName='transform' type='rotate' from='0 185 160' to='360 185 160' dur='20s' repeatCount='indefinite' />
        </path>
        <path d='M40,180 Q120,140 200,180' stroke='#50CD89' strokeWidth='3' fill='none' opacity='0.6'>
          <animate attributeName='stroke-dasharray' values='0 300;300 0' dur='5s' repeatCount='indefinite' />
        </path>
      </svg>
    ),
  },
  PURCHASES: {
    icon: 'purchase',
    accent: '#F1416C',
    bg: (
      <svg viewBox='0 0 240 240' xmlns='http://www.w3.org/2000/svg'>
        <rect x='130' y='40' width='60' height='70' rx='6' fill='#F1416C' opacity='0.8'>
          <animate attributeName='y' values='40;30;40' dur='3s' repeatCount='indefinite' />
        </rect>
        <rect x='60' y='120' width='80' height='60' rx='6' fill='#F1416C' opacity='0.5'>
          <animate attributeName='y' values='120;130;120' dur='3.5s' repeatCount='indefinite' />
        </rect>
        <circle cx='50' cy='50' r='20' fill='#F1416C'>
          <animate attributeName='r' values='20;28;20' dur='4s' repeatCount='indefinite' />
        </circle>
      </svg>
    ),
  },
  EXPENSES: {
    icon: 'wallet',
    accent: '#FFC700',
    bg: (
      <svg viewBox='0 0 240 240' xmlns='http://www.w3.org/2000/svg'>
        <circle cx='60' cy='60' r='30' fill='#FFC700'>
          <animate attributeName='cx' values='60;80;60' dur='5s' repeatCount='indefinite' />
        </circle>
        <circle cx='180' cy='100' r='25' fill='#FFC700' opacity='0.7'>
          <animate attributeName='cy' values='100;120;100' dur='4s' repeatCount='indefinite' />
        </circle>
        <circle cx='140' cy='180' r='35' fill='#FFC700' opacity='0.5'>
          <animate attributeName='cx' values='140;120;140' dur='6s' repeatCount='indefinite' />
        </circle>
      </svg>
    ),
  },
  REPORTS: {
    icon: 'chart-line',
    accent: '#7239EA',
    bg: (
      <svg viewBox='0 0 240 240' xmlns='http://www.w3.org/2000/svg'>
        <polyline points='30,180 80,140 120,160 170,90 210,110' fill='none' stroke='#7239EA' strokeWidth='4' strokeLinecap='round' strokeLinejoin='round'>
          <animate attributeName='stroke-dasharray' values='0 400;400 0' dur='6s' repeatCount='indefinite' />
        </polyline>
        <circle cx='80' cy='140' r='6' fill='#7239EA'>
          <animate attributeName='r' values='6;10;6' dur='2s' repeatCount='indefinite' />
        </circle>
        <circle cx='170' cy='90' r='6' fill='#7239EA'>
          <animate attributeName='r' values='6;10;6' dur='2.5s' repeatCount='indefinite' />
        </circle>
      </svg>
    ),
  },
  MASTER_DATA: {
    icon: 'data',
    accent: '#17C653',
    bg: (
      <svg viewBox='0 0 240 240' xmlns='http://www.w3.org/2000/svg'>
        <ellipse cx='120' cy='70' rx='60' ry='14' fill='#17C653' opacity='0.8' />
        <rect x='60' y='70' width='120' height='50' fill='#17C653' opacity='0.5' />
        <ellipse cx='120' cy='120' rx='60' ry='14' fill='#17C653' opacity='0.6' />
        <rect x='60' y='120' width='120' height='50' fill='#17C653' opacity='0.3' />
        <ellipse cx='120' cy='170' rx='60' ry='14' fill='#17C653' opacity='0.4'>
          <animate attributeName='ry' values='14;18;14' dur='3s' repeatCount='indefinite' />
        </ellipse>
      </svg>
    ),
  },
  USER_MANAGEMENT: {
    icon: 'people',
    accent: '#009EF7',
    bg: (
      <svg viewBox='0 0 240 240' xmlns='http://www.w3.org/2000/svg'>
        <circle cx='90' cy='90' r='24' fill='#009EF7' />
        <path d='M50,160 Q90,120 130,160 L130,200 L50,200 Z' fill='#009EF7' opacity='0.7' />
        <circle cx='170' cy='80' r='20' fill='#009EF7' opacity='0.6'>
          <animate attributeName='cy' values='80;70;80' dur='3s' repeatCount='indefinite' />
        </circle>
        <path d='M140,150 Q170,120 200,150 L200,190 L140,190 Z' fill='#009EF7' opacity='0.4' />
      </svg>
    ),
  },
  SETTINGS: {
    icon: 'gear',
    accent: '#7E8299',
    bg: (
      <svg viewBox='0 0 240 240' xmlns='http://www.w3.org/2000/svg'>
        <g transform='translate(80 80)'>
          <path d='M0,-40 L10,-38 L14,-28 L24,-24 L32,-30 L40,-22 L34,-14 L38,-4 L48,0 L48,10 L38,14 L34,24 L40,32 L32,40 L24,34 L14,38 L10,48 L0,48 L-4,38 L-14,34 L-22,40 L-30,32 L-24,24 L-28,14 L-38,10 L-38,0 L-28,-4 L-24,-14 L-30,-22 L-22,-30 L-14,-24 L-4,-28 Z' fill='#7E8299' opacity='0.4'>
            <animateTransform attributeName='transform' type='rotate' from='0' to='360' dur='20s' repeatCount='indefinite' />
          </path>
        </g>
        <g transform='translate(170 160)'>
          <path d='M0,-24 L6,-22 L8,-16 L14,-14 L18,-18 L24,-12 L20,-8 L22,-2 L28,0 L28,6 L22,8 L20,14 L24,20 L18,24 L14,20 L8,22 L6,28 L0,28 L-2,22 L-8,20 L-14,24 L-18,18 L-14,14 L-16,8 L-22,6 L-22,0 L-16,-2 L-14,-8 L-18,-14 L-14,-18 L-8,-14 L-2,-16 Z' fill='#7E8299' opacity='0.3'>
            <animateTransform attributeName='transform' type='rotate' from='360' to='0' dur='16s' repeatCount='indefinite' />
          </path>
        </g>
      </svg>
    ),
  },
  SYSTEM: {
    icon: 'technology-4',
    accent: '#00A3FF',
    bg: (
      <svg viewBox='0 0 240 240' xmlns='http://www.w3.org/2000/svg'>
        <circle cx='120' cy='120' r='70' fill='none' stroke='#00A3FF' strokeWidth='2' opacity='0.3'>
          <animate attributeName='r' values='70;85;70' dur='4s' repeatCount='indefinite' />
        </circle>
        <circle cx='120' cy='120' r='50' fill='none' stroke='#00A3FF' strokeWidth='2' opacity='0.5'>
          <animate attributeName='r' values='50;65;50' dur='3s' repeatCount='indefinite' />
        </circle>
        <circle cx='120' cy='120' r='20' fill='#00A3FF'>
          <animate attributeName='r' values='20;26;20' dur='2s' repeatCount='indefinite' />
        </circle>
      </svg>
    ),
  },
}

const getTheme = (categoryName: string): CategoryTheme => THEMES[categoryName] || FALLBACK_THEME

const HelpWelcome: React.FC<Props> = ({categories, isLoading}) => {
  if (isLoading) {
    return (
      <div className='text-center py-10'>
        <Spinner animation='border' variant='primary' />
        <p className='text-muted mt-3'>Loading help articles...</p>
      </div>
    )
  }

  return (
    <div>
      <div className='help-welcome'>
        <KTIcon iconName='book-open' className='welcome-icon d-block' />
        <h2>Help & Documentation</h2>
        <p>
          Browse articles below or use the search bar to find what you need.
          Click the <strong>?</strong> button on any page for context-specific help.
        </p>
      </div>

      {categories.length === 0 ? (
        <div className='text-center py-5'>
          <p className='text-muted'>No help articles are available yet.</p>
        </div>
      ) : (
        <div className='row g-4'>
          {categories.map((cat) => {
            const theme = getTheme(cat.name)
            return (
              <div key={cat.name} className='col-12 col-md-6'>
                <div className='card shadow-sm h-100 help-category-card'>
                  <div className='help-category-bg' aria-hidden='true'>
                    {theme.bg}
                  </div>
                  <div className='card-header border-0 pt-5 pb-0 help-category-content'>
                    <h5
                      className='card-title d-flex align-items-center gap-3 mb-0'
                      style={{color: theme.accent}}
                    >
                      <span
                        className='help-category-icon-wrap'
                        style={{backgroundColor: `${theme.accent}1A`}}
                      >
                        <KTIcon iconName={theme.icon} className='fs-2' />
                      </span>
                      <span className='fw-bold'>{cat.displayName}</span>
                    </h5>
                  </div>
                  <div className='card-body pt-4 help-category-content'>
                    <ul className='list-unstyled mb-0'>
                      {cat.documents.map((doc) => (
                        <li key={doc.pageId} className='mb-3'>
                          <Link
                            to={`/help/${doc.pageId}`}
                            className='help-doc-link d-flex align-items-start gap-3'
                          >
                            <span
                              className='help-doc-icon-wrap'
                              style={{backgroundColor: `${theme.accent}14`, color: theme.accent}}
                            >
                              <KTIcon iconName={(doc.icon || 'document').replace(/^ki-/, '')} className='fs-5' />
                            </span>
                            <div className='flex-grow-1'>
                              <span className='fw-semibold text-gray-800'>{doc.title}</span>
                              <span className='d-block text-muted fs-7'>{doc.shortDescription}</span>
                            </div>
                            <KTIcon iconName='arrow-right' className='fs-6 text-gray-400 help-doc-arrow' />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export {HelpWelcome}
